import React, { useState, useEffect, useRef } from 'react';

interface TypewriterTextProps {
  text: string;
  onComplete?: () => void;
  speed?: number;
}

/**
 * Typewriter animation component using setInterval.
 * Displays text character by character with configurable speed.
 *
 * @param text - The text to display with typewriter effect
 * @param onComplete - Callback when animation finishes
 * @param speed - Milliseconds per character (default: 6ms)
 */
export const TypewriterText: React.FC<TypewriterTextProps> = ({
  text,
  onComplete,
  speed = 6,
}) => {
  const [displayedText, setDisplayedText] = useState('');
  const [isComplete, setIsComplete] = useState(false);

  // Store onComplete in a ref to avoid restarting animation when callback changes
  const onCompleteRef = useRef(onComplete);
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  // Reset state when text changes to allow new animations
  useEffect(() => {
    setDisplayedText('');
    setIsComplete(false);
  }, [text]);

  useEffect(() => {
    if (isComplete) return;

    let index = 0;
    const timer = setInterval(() => {
      if (index < text.length) {
        setDisplayedText(text.slice(0, index + 1));
        index++;
      } else {
        clearInterval(timer);
        setIsComplete(true);
        onCompleteRef.current?.();
      }
    }, speed);

    return () => clearInterval(timer);
  }, [text, speed, isComplete]);

  return <>{displayedText}</>;
};

TypewriterText.displayName = 'TypewriterText';
